import React, { useEffect, useRef, useState } from 'react';
import { Box, Card, CardBody, Flex, Link, Text } from '@chakra-ui/react';

interface Props {
    name: string;
    description?: string;
    leftBottomContent: React.ReactNode;
    rightBottomContent: React.ReactNode;
}

const MAX_LINES_COLLAPSED = 3;

const InvoiceItemCard: React.FC<Props> = ({
    name,
    description,
    leftBottomContent,
    rightBottomContent
}) => {
    const textRef = useRef<HTMLParagraphElement>(null);
    const [isExpanded, setIsExpanded] = useState(false);
    const [isTruncated, setIsTruncated] = useState(false);

    useEffect(() => {
        // Only check for truncation when the text is in collapsed state
        if (textRef.current && !isExpanded) {
            // Check if the content is overflowing (scrollHeight > clientHeight)
            const truncated = textRef.current.scrollHeight > textRef.current.clientHeight;
            setIsTruncated(truncated);
        }
    }, [description, isExpanded]);

    return (
        <Card bg="transparent" border="1px solid" borderColor="gray.200" borderRadius="8px">
            <CardBody padding="16px">
                <Box>
                    <Text fontWeight="semibold" fontSize="16px">
                        {name}
                    </Text>
                    {description && (
                        <Box mt="8px">
                            <Text
                                ref={textRef}
                                fontSize="14px"
                                color="gray.600"
                                whiteSpace="pre-wrap"
                                noOfLines={isExpanded ? undefined : MAX_LINES_COLLAPSED}
                            >
                                {description}
                            </Text>
                            {isTruncated && (
                                <Link
                                    color="brand.primary"
                                    fontSize="14px"
                                    fontFamily="'Open Sans', sans-serif"
                                    onClick={() => setIsExpanded(!isExpanded)}
                                    cursor="pointer"
                                    textDecoration="underline"
                                    mt="4px"
                                    display="inline-block"
                                >
                                    {isExpanded ? 'See Less' : 'See More'}
                                </Link>
                            )}
                        </Box>
                    )}
                </Box>
                <Flex
                    justify="space-between"
                    align="center"
                    mt="12px"
                    pt="12px"
                    borderTop="1px solid"
                    borderColor="gray.200"
                >
                    {leftBottomContent}
                    {rightBottomContent}
                </Flex>
            </CardBody>
        </Card>
    );
};

export default InvoiceItemCard;
